import currencyFormatter from 'currency-formatter'
import store from '@/store'
import i18n from '@/plugins/i18n'
import { getCurrencySymbolKeyByCurrency } from '@/utils/i18n'
import { getDecimalDigitsByCurrency } from '@/utils/currency-config'

/**
 * @param {Object} totalAmount
 * @param {Object} options
 * @return {String}
 */
function amountFormatter(totalAmount, options = {}) {
  const { amount = 0, currency = 'SAR' } = totalAmount || {}
  const currencySymbolKey = getCurrencySymbolKeyByCurrency(currency)
  const symbol = i18n.t(currencySymbolKey)
  const locale = i18n.locale.replace('-', '_')
  const [lang] = locale.split('_')

  const amountFormatted = currencyFormatter.format(amount, {
    symbol,
    precision: options?.precision ?? getDecimalDigitsByCurrency(currency),
    format: lang === 'ar' ? '%v %s' : '%s %v',
  })

  if (options?.removeZero) {
    return amountFormatted.replace(/\.0{2,3}$/, '')
  }

  return amountFormatted
}

/**
 * @param {Object} totalAmount
 */
function amountFormatToHTML(totalAmount) {
  const currencyValue = store.getters['session/orderCurrency']
  const { amount = 0, currency = currencyValue } = totalAmount || {}
  const currencySymbolKey = getCurrencySymbolKeyByCurrency(currency)
  const symbol = i18n.t(currencySymbolKey)
  const locale = i18n.locale.replace('-', '_')
  const [lang] = locale.split('_')

  // Negative numbers break the currencyFormatter;
  // Pass amount as absolute value and pre-prend '-' in format if negative
  const sign = amount < 0 ? '-' : ''

  return currencyFormatter.format(Math.abs(amount), {
    symbol,
    precision: getDecimalDigitsByCurrency(currency),
    format: lang === 'ar' ? `%v${sign} %s` : `%s ${sign}%v`,
  })
}

/**
 * @param {Object} phone
 */
function phoneNumberFormatter(phone) {
  const { phoneNumber = '', countryCode = '' } = phone || {}
  const formattedPhoneNumber = phoneNumber.replace(/^0/i, '')

  if (!countryCode || !phoneNumber) {
    return ''
  }

  return `+${countryCode} ${formattedPhoneNumber}`
}

function displayChevronIconByDirection(
  isRightToLeftDirection,
  reverse = false
) {
  if (reverse) {
    return isRightToLeftDirection ? 'chevron-left' : 'chevron-right'
  }

  return isRightToLeftDirection ? 'chevron-right' : 'chevron-left'
}

function getIconNameByDirection(direction, mapping = {}) {
  return mapping[direction]
}

function getIconName(name) {
  const iconGroup = {
    question: {
      en: 'question-icon',
      ar: 'question-icon-ar',
    },
    questionPopup: {
      en: 'question-popup',
      ar: 'question-popup-ar',
    },
  }[name]

  const lang = store.getters['application/currentLang']

  return iconGroup[lang]
}

function timerFormatter(timer) {
  if (!TypeChecker.isNumber(timer) || timer <= 0) {
    return '--:--'
  }

  const minute = Math.floor(timer / 60)
  const second = timer % 60
  const minuteFormatted = minute < 10 ? `0${minute}` : minute
  const secondFormatted = second < 10 ? `0${second}` : second

  return `${minuteFormatted}:${secondFormatted}`
}

function formatDurationMmSs(remainMs) {
  if (Number.isNaN(remainMs) || !TypeChecker.isNumber(remainMs)) {
    return '--:--'
  }

  remainMs = Math.max(remainMs, 0)

  const totalSeconds = Math.round(remainMs / 1000)

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)

  const minutesFormatted = String(minutes).padStart(2, '0')
  const secondsFormatted = String(seconds).padStart(2, '0')

  return `${minutesFormatted}:${secondsFormatted}`
}

export {
  amountFormatter,
  amountFormatToHTML,
  phoneNumberFormatter,
  displayChevronIconByDirection,
  getIconName,
  getIconNameByDirection,
  timerFormatter,
  formatDurationMmSs,
}
